import React, {useState, useMemo, useCallback, useRef, MouseEvent} from 'react';

import {
    Button,
    Text,
    PriorityIcon,
    Flex,
    CheckBox,
    noop,
    Icon
} from '@priority-software/priority-style-react';
import {v4 as uuidv4} from 'uuid';
import {ColumnType} from '../../../../../AppSearches/types/column';
import {getLocalizedText, texts} from '../../../../../../constants/TextConst';
import {Column} from '../../../../../../stores/Form';
import {Operator} from '../../../../../../_legacy/Searches/consts/operators';
import FilterBox from '../../../../../../features/AppSearches/componenets/FilterBox/FilterBox';
import {serializeField} from '../../../../common/serializeFiels';
import {getBaseColumnType} from '../../../../../AppSearches/types/column';
import {updateColumnFilter} from '../../../../actions/filters';
import {ISearchCondition} from '../../../../../AppSearches/types/filters';
import {patchFetchConditions} from '../../../../utils/filters';

import styles from './styles.module.scss';
import {PatchFilter} from '../../../AdvancedSearch/AdvancedSearchDrawer';
import {priority} from '@priority-software/priority-proto-api/lib';
import {ToggleButton} from '../../../../../../components/ToggleButton';
import {CheckedValue, ToggleBtnLabel} from '../../../../../../components/ToggleButton/enum';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {isFilterChanged} from './utils';

type Props = {
    column: Column;
    onClose: () => void;
    setFilterChange: (arg: boolean) => void;
};

export const FilterDropdownContent = ({column: fetchColumn, onClose, setFilterChange}: Props) => {
    const saveBtnRef = useRef(null);
    const [isDisabled, setIsDisabled] = useState(true);
    const [checkedOperator, setCheckedOperator] = useState<CheckedValue>(() => {
        const fetchFilter = fetchColumn.parentForm.filters.find(
            filter => filter?.Condition[0]?.ColumnID === fetchColumn.id
        );
        return fetchFilter?.Operator === priority.netitems.shared.LogicalOperator.And ||
            fetchFilter?.Operator === undefined
            ? CheckedValue.left
            : CheckedValue.right;
    });

    const [filter, setFilter] = useState<PatchFilter>(() => {
        const filter = fetchColumn.parentForm.filters.find(
            filter => filter?.Condition[0]?.ColumnID === fetchColumn.id
        );
        return {
            Condition: patchFetchConditions(fetchColumn, filter),
            Operator:
                filter?.Operator === 0
                    ? priority.netitems.shared.LogicalOperator.And
                    : priority.netitems.shared.LogicalOperator.Or
        };
    });

    const parentForm = fetchColumn.parentForm;
    const column = useMemo(() => serializeField(fetchColumn), [fetchColumn]);

    const showIgnoreCaseCheckbox =
        getBaseColumnType(fetchColumn.Type) === ColumnType.Text
            ? filter.Condition.map(
                  cond => cond.operator !== Operator.Equals && cond.operator !== Operator.NotEquals
              )
            : [false];

    const onRemoveFilter = (columnID: number | Long.Long, index: number) => {
        setFilter(prev => ({
            ...prev,
            Condition: prev.Condition.filter((cond, idx) => index !== idx)
        }));
        setIsDisabled(false);
    };

    const handleAddCondition = useCallback(() => {
        setFilter(prev => ({
            ...prev,
            Condition: [...prev.Condition, ...patchFetchConditions(fetchColumn)]
        }));
    }, []);

    const handleToggleOperator = useCallback((value: CheckedValue) => {
        setFilter(prev => ({
            ...prev,
            Operator:
                value === CheckedValue.left
                    ? priority.netitems.shared.LogicalOperator.And
                    : priority.netitems.shared.LogicalOperator.Or
        }));
        setIsDisabled(false);
        setCheckedOperator(value);
    }, []);

    const toggleIgnoreCase = useCallback(
        (index: number) => {
            setIsDisabled(false);
            setFilter(prev => ({
                ...prev,
                Condition: prev.Condition.map((cond, idx) => {
                    return idx === index ? {...cond, ignoreCase: !cond.ignoreCase} : cond;
                })
            }));
            setFilterChange(true);
        },
        [setFilterChange]
    );
    const handleUpdateFilter = (condition: ISearchCondition, index: number) => {
        setFilter(prev => {
            if (
                prev.Condition[index].values.toString() !== condition.values.toString() ||
                prev.Condition[index].operator !== condition.operator ||
                prev.Condition[index].ignoreCase !== condition.ignoreCase
            ) {
                if (
                    prev.Condition[index].type === ColumnType.Time &&
                    condition.values.toString() === ','
                ) {
                    setFilterChange(false);
                    return prev;
                }
                setIsDisabled(false);
                setFilterChange(true);
            }

            return {
                ...prev,
                Condition: [
                    ...prev.Condition.slice(0, index),
                    {
                        ...prev.Condition[index],
                        values: condition.values,
                        operator: condition.operator,
                        dateEnum: condition.dateEnum
                    },
                    ...prev.Condition.slice(index + 1)
                ]
            };
        });
    };

    const handleClearFilter = useCallback(
        e => {
            e.stopPropagation();

            let isChanged = false;
            setFilter(prev => {
                const clearedConditions = prev.Condition.filter(
                    item => item.operator !== Operator.Empty && item.operator !== Operator.NotEmpty
                ).map(item => {
                    return {
                        ...item,
                        id: uuidv4(),
                        values: [],
                        dateEnum: []
                    };
                });

                // If no conditions remain after filtering, create a default condition
                const finalConditions =
                    clearedConditions.length === 0
                        ? patchFetchConditions(fetchColumn)
                        : clearedConditions;

                isChanged = isFilterChanged(finalConditions, prev.Condition);

                return isChanged ? {...prev, Condition: finalConditions} : prev;
            });

            if (isChanged) {
                setIsDisabled(false);
                setFilterChange(true);
            }
        },
        [setFilterChange, fetchColumn]
    );

    const onSearch = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            updateColumnFilter(parentForm, filter);
            setFilterChange(false);

            onClose();
        },
        [onClose, filter, parentForm, setFilterChange]
    );

    const onSelectClose = () => {
        saveBtnRef.current?.focus();
    };

    return (
        <>
            {filter.Condition.map((condition, idx) => (
                <div className={styles.container} key={condition.id}>
                    <FilterBox
                        isCancelBtnOnHover={filter.Condition.length > 1}
                        withTitle={false}
                        filter={condition}
                        column={column}
                        session={parentForm.session}
                        contentClasses={styles.filterBoxContent}
                        dropdownWidth={540}
                        onRemoveFilter={id => onRemoveFilter(id, idx)}
                        onUpdateFilter={cond => handleUpdateFilter(cond, idx)}
                        onSelectClose={onSelectClose}
                        scrollToBottom={noop}
                        withSort={false}
                    />
                    {showIgnoreCaseCheckbox[idx] && (
                        <CheckBox
                            {...createDataTestId('column-filter-ignore-case', true)}
                            onChange={() => toggleIgnoreCase(idx)}
                            checked={filter.Condition[idx]?.ignoreCase}
                        >
                            <Text
                                skin="label-2"
                                textColor="secondary-400"
                                componentClasses={styles.whiteSpace}
                            >
                                {getLocalizedText(texts.caseSensitive)}
                            </Text>
                        </CheckBox>
                    )}
                </div>
            ))}
            <Flex
                componentClasses={styles.filterFooterButtons}
                justifyContent={'space-between'}
                {...createDataTestId('column-filter-footer', true)}
            >
                {filter.Condition.length === 2 ? (
                    <ToggleButton
                        checked={checkedOperator}
                        onChange={handleToggleOperator}
                        left={ToggleBtnLabel.And}
                        right={ToggleBtnLabel.Or}
                    />
                ) : (
                    <Button
                        onClick={handleAddCondition}
                        componentClasses={styles.AddBtn}
                        skin="secondary"
                    >
                        <Icon
                            icon={PriorityIcon.add}
                            skin="primary"
                            componentClasses={styles.iconPlus}
                        />
                    </Button>
                )}

                <Flex justifyContent={'flex-end'}>
                    <Button
                        skin="text"
                        componentClasses={styles.filterFooterLinkButton}
                        disabled={false}
                        prefixIcon={PriorityIcon.clear}
                        onClick={handleClearFilter}
                    >
                        {getLocalizedText(texts.clearSearch)}
                    </Button>
                    <Button
                        inputRef={saveBtnRef}
                        disabled={isDisabled}
                        size="medium"
                        width="regularFixed"
                        onClick={onSearch}
                    >
                        {getLocalizedText(texts.apply)}
                    </Button>
                </Flex>
            </Flex>
        </>
    );
};
